import { useState } from "react";
import PageLayout from "../../components/PageLayout";
import { getInvestorData } from "../../lib/hooks";
import { useResponsive } from "../../lib/useResponsive";

// Helper function based on "image_470e5c.jpg" framework
const getPartnerTier = (equity: number) => {
  if (equity >= 1500000) return { name: "Diamond", title: "Elite Partner", color: "#b026ff", bg: "rgba(176, 38, 255, 0.1)" };
  if (equity >= 1000000) return { name: "Platinum", title: "Principal Partner", color: "#4fa8ff", bg: "rgba(79, 168, 255, 0.1)" };
  if (equity >= 500000) return { name: "Gold", title: "Senior Partner", color: "#ffd700", bg: "rgba(255, 215, 0, 0.1)" };
  if (equity >= 100000) return { name: "Silver", title: "Vanguard Partner", color: "#ffffff", bg: "rgba(255, 255, 255, 0.1)" };
  return { name: "Bronze", title: "Rising Partner", color: "#cd7f32", bg: "rgba(205, 127, 50, 0.1)" };
};

interface InvestorDataRow {
  name: string;
  investment_amount: number;
  portfolio_amount: number;
  ownership_percentage: string;
  maturity_date: string;
}

export default function InvestorsPage() {
  const { data: rawRows, loading } = getInvestorData() as {
    data: any;
    loading: boolean;
  };
  const [activeBranch, setActiveBranch] = useState<string | null>(null);
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  // Robust parsing logic to prevent "l.map is not a function"
  // Handles arrays directly, nested objects like { investors: [...] }, or object wrappers like { data: [...] }
  const getInvestorsArray = (): InvestorDataRow[] => {
    if (!rawRows) return [];
    if (Array.isArray(rawRows)) return rawRows;
    if (typeof rawRows === "object") {
      if (Array.isArray(rawRows.investors)) return rawRows.investors;
      if (Array.isArray(rawRows.data)) return rawRows.data;
      if (Array.isArray(rawRows.rows)) return rawRows.rows;
    }
    return [];
  };

  const investors = getInvestorsArray();

  return (
    <PageLayout
      eyebrow="SSN Partners • 2026"
      title={<>Investor Registry &amp; <em>Partner Tiers</em>.</>}
      subtitle="Real-time equity breakdown and ecosystem tier rankings."
    >
      {loading ? (
        /* Simple Loading State Wrapper */
        <div style={{ ...S.card, ...S.bxStd, padding: 32, textAlign: "center", color: "var(--text-secondary)", marginBottom: 40 }}>
          Loading partner registry metrics...
        </div>
      ) : investors.length === 0 ? (
        /* Empty State Wrapper */
        <div style={{ ...S.card, ...S.bxStd, padding: 32, textAlign: "center", color: "var(--text-muted)", marginBottom: 40 }}>
          No investor data available.
        </div>
      ) : isMobileOrTablet ? (
        /* Mobile/Tablet Card Stack View */
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 40 }}>
          {investors.map((inv, idx) => {
            const tier = getPartnerTier(inv.portfolio_amount || 0);
            return (
              <div key={idx} style={{ ...S.card, ...S.bxStd, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text-primary)", textTransform: "capitalize" }}>
                      {inv.name}
                    </h4>
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Matures: {inv.maturity_date}</span>
                  </div>
                  <span style={{ ...S.tierBadge, backgroundColor: tier.bg, color: tier.color, borderColor: tier.color }}>
                    {tier.name}
                  </span>
                </div>
                
                <div style={S.mobileGrid}>
                  <div>
                    <span style={S.label}>Tier Role</span>
                    <span style={{ ...S.val, color: tier.color }}>{tier.title}</span>
                  </div>
                  <div>
                    <span style={S.label}>Ownership</span>
                    <span style={S.val}>{inv.ownership_percentage}</span>
                  </div>   
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop Structured Data Table View */
        <div style={S.tableWrapper}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Investor Name</th>
                <th style={S.th}>Membership Tier</th>
                <th style={S.th}>Ownership %</th>
                <th style={S.th}>Maturity Date</th>
              </tr>
            </thead>
            <tbody>
              {investors.map((inv, idx) => {
                const tier = getPartnerTier(inv.portfolio_amount || 0);
                return (
                  <tr key={idx} style={S.tr}>
                    <td style={{ ...S.td, fontWeight: 600, color: "var(--text-primary)", textTransform: "capitalize" }}>{inv.name}</td>
                    <td style={S.td}>
                      <span style={{ ...S.tierBadge, backgroundColor: tier.bg, color: tier.color, borderColor: `${tier.color}33` }}>
                        {tier.name} ({tier.title})
                      </span>
                    </td>
                    <td style={S.td}>{inv.ownership_percentage}</td>
                    <td style={{ ...S.td, color: "var(--text-secondary)" }}>{inv.maturity_date}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --- MEMBERSHIP TIERS FRAMEWORK SECTION --- */}
      <div style={S.frameworkContainer}>
        <div style={S.frameworkHeader}>
          5 exclusive membership tiers — dual qualification criteria recognizing equity contribution and leadership role within the SSN ecosystem.
        </div>

        {/* BRONZE TIER */}
        <div style={{ ...S.frameworkCard, borderColor: "rgba(205, 127, 50, 0.3)", background: "linear-gradient(90deg, rgba(205, 127, 50, 0.05) 0%, rgba(0,0,0,0) 100%)" }}>
          <div style={S.frameworkLeftColumn}>
            <div style={{ ...S.circleIcon, background: "radial-gradient(circle, #f3a661 0%, #cd7f32 100%)" }}>✦</div>
            <div style={{ ...S.tierLabel, color: "#cd7f32" }}>BRONZE</div>
          </div>
          <div style={S.frameworkRightColumn}>
            <div style={{ ...S.partnerTitle, color: "#f3a661" }}>✦ RISING PARTNER</div>
            <div style={S.mainValue}>Under $100K</div>
            <div style={S.descText}>New Partners with total equity below $100,000 and no Branch responsibility.</div>
          </div>
        </div>

        {/* SILVER TIER */}
        <div style={{ ...S.frameworkCard, borderColor: "rgba(255, 255, 255, 0.15)", background: "linear-gradient(90deg, rgba(255, 255, 255, 0.03) 0%, rgba(0,0,0,0) 100%)" }}>
          <div style={S.frameworkLeftColumn}>
            <div style={{ ...S.circleIcon, background: "radial-gradient(circle, #ffffff 0%, #a1a1a1 100%)", color: "#333" }}>★</div>
            <div style={{ ...S.tierLabel, color: "#ffffff" }}>SILVER</div>
          </div>
          <div style={S.frameworkRightColumn}>
            <div style={{ ...S.partnerTitle, color: "#ffffff" }}>★ VANGUARD PARTNER</div>
            <div style={S.mainValue}>Under $500K</div>
            <div style={S.descText}>Branch Officers, C-Suite Executives &amp; Original Karyakartas. Auto-assigned upon qualification.</div>
            <div style={{ marginTop: 10 }}>
              <span style={S.inlineBadge}>★ AUTO-ASSIGNED · BRANCH LEADERSHIP &amp; KEY ROLES</span>
            </div>
          </div>
        </div>

        {/* GOLD TIER */}
        <div style={{ ...S.frameworkCard, borderColor: "rgba(255, 215, 0, 0.25)", background: "linear-gradient(90deg, rgba(255, 215, 0, 0.03) 0%, rgba(0,0,0,0) 100%)" }}>
          <div style={S.frameworkLeftColumn}>
            <div style={{ ...S.circleIcon, background: "radial-gradient(circle, #fff093 0%, #ffd700 100%)", color: "#665400" }}>♔</div>
            <div style={{ ...S.tierLabel, color: "#ffd700" }}>GOLD</div>
          </div>
          <div style={S.frameworkRightColumn}>
            <div style={{ ...S.partnerTitle, color: "#ffd700" }}>♔ SENIOR PARTNER</div>
            <div style={S.splitGrid}>
              <div>
                <div style={S.splitLabel}>⚑ LEADERSHIP PARTNERS</div>
                <div style={{ ...S.splitValue, color: "#ffd700" }}>$250K+</div>
                <div style={S.descText}>Total equity exceeding $250,000</div>
              </div>
              <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: 20 }}>
                <div style={S.splitLabel}>👤 OTHER PARTNERS</div>
                <div style={{ ...S.splitValue, color: "#ffd700" }}>$500K+</div>
                <div style={S.descText}>Total equity exceeding $500,000</div>
              </div>
            </div>
          </div>
        </div>

        {/* PLATINUM TIER */}
        <div style={{ ...S.frameworkCard, borderColor: "rgba(79, 168, 255, 0.25)", background: "linear-gradient(90deg, rgba(79, 168, 255, 0.03) 0%, rgba(0,0,0,0) 100%)" }}>
          <div style={S.frameworkLeftColumn}>
            <div style={{ ...S.circleIcon, background: "radial-gradient(circle, #bce0ff 0%, #4fa8ff 100%)" }}>✦</div>
            <div style={{ ...S.tierLabel, color: "#4fa8ff" }}>PLATINUM</div>
          </div>
          <div style={S.frameworkRightColumn}>
            <div style={{ ...S.partnerTitle, color: "#4fa8ff" }}>✦ PRINCIPAL PARTNER</div>
            <div style={S.splitGrid}>
              <div>
                <div style={S.splitLabel}>⚑ LEADERSHIP PARTNERS</div>
                <div style={{ ...S.splitValue, color: "#4fa8ff" }}>$500K+</div>
                <div style={S.descText}>Total equity exceeding $500,000</div>
              </div>
              <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: 20 }}>
                <div style={S.splitLabel}>👤 OTHER PARTNERS</div>
                <div style={{ ...S.splitValue, color: "#4fa8ff" }}>$1M+</div>
                <div style={S.descText}>Total equity exceeding $1,000,000</div>
              </div>
            </div>
          </div>
        </div>

        {/* DIAMOND TIER */}
        <div style={{ ...S.frameworkCard, borderColor: "rgba(176, 38, 255, 0.25)", background: "linear-gradient(90deg, rgba(176, 38, 255, 0.03) 0%, rgba(0,0,0,0) 100%)" }}>
          <div style={S.frameworkLeftColumn}>
            <div style={{ ...S.circleIcon, background: "radial-gradient(circle, #e6bfff 0%, #b026ff 100%)" }}>♦</div>
            <div style={{ ...S.tierLabel, color: "#b026ff" }}>DIAMOND</div>
          </div>
          <div style={S.frameworkRightColumn}>
            <div style={{ ...S.partnerTitle, color: "#b026ff" }}>✦ ELITE PARTNER</div>
            <div style={S.splitGrid}>
              <div>
                <div style={S.splitLabel}>⚑ LEADERSHIP PARTNERS</div>
                <div style={{ ...S.splitValue, color: "#b026ff" }}>$1M+</div>
                <div style={S.descText}>Total equity exceeding $1,000,000</div>
              </div>
              <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: 20 }}>
                <div style={S.splitLabel}>👤 OTHER PARTNERS</div>
                <div style={{ ...S.splitValue, color: "#b026ff" }}>$1.5M+</div>
                <div style={S.descText}>Total equity exceeding $1,500,000</div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  bxStd: { background: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.07)" },
  card: { borderRadius: 12, display: "flex", flexDirection: "column" },
  mobileGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 },
  label: { display: "block", fontSize: 11, color: "var(--text-muted)", marginBottom: 2, textTransform: "uppercase" },
  val: { display: "block", fontSize: 13, fontWeight: 600, color: "var(--text-primary)" },
  
  tableWrapper: {
    width: "100%", overflowX: "auto", background: "rgba(255, 255, 255, 0.02)",
    border: "1px solid rgba(255, 255, 255, 0.06)", borderRadius: 12, marginBottom: 40
  },
  table: { width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 14 },
  th: { padding: "16px", background: "rgba(255, 255, 255, 0.03)", color: "var(--text-muted)", fontWeight: 500, fontSize: 12, textTransform: "uppercase" },
  tr: { borderBottom: "1px solid rgba(255, 255, 255, 0.05)" },
  td: { padding: "16px", color: "var(--text-secondary)" },
  
  tierBadge: {
    display: "inline-block", padding: "4px 10px", borderRadius: "20px", fontSize: 11,
    fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", border: "1px solid transparent"
  },
  tagline: { textAlign: "center", fontStyle: "italic", color: "var(--text-muted)", marginTop: 24 },

  frameworkContainer: { display: "flex", flexDirection: "column", gap: 16, marginTop: 24, width: "100%" },
  frameworkHeader: {
    textAlign: "center", fontSize: 13, color: "var(--text-secondary)", opacity: 0.8,
    padding: "16px 24px", border: "1px solid rgba(255, 255, 255, 0.08)", borderRadius: 8,
    background: "rgba(255,255,255, 0.01)", marginBottom: 8, lineHeight: "1.5"
  },
  frameworkCard: {
    display: "flex", borderRadius: 12, border: "1px solid", padding: "20px 24px",
    alignItems: "stretch", minHeight: 110, boxSizing: "border-box"
  },
  frameworkLeftColumn: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    width: 100, marginRight: 24, borderRight: "1px solid rgba(255,255,255,0.05)", paddingRight: 16
  },
  circleIcon: {
    width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", fontWeight: "bold", fontSize: 18, color: "#fff", marginBottom: 8,
    boxShadow: "0 4px 10px rgba(0,0,0,0.3)"
  },
  tierLabel: { fontSize: 11, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase" },
  frameworkRightColumn: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" },
  partnerTitle: { fontSize: 11, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 },
  mainValue: { fontSize: 28, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6, letterSpacing: "-0.5px" },
  descText: { fontSize: 12, color: "var(--text-muted)", lineHeight: "1.4" },
  inlineBadge: {
    display: "inline-block", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 20, padding: "4px 12px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "0.5px"
  },
  splitGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 4 },
  splitLabel: { fontSize: 10, fontWeight: 700, color: "var(--text-muted)", letterSpacing: "0.5px", marginBottom: 4 },
  splitValue: { fontSize: 24, fontWeight: 800, marginBottom: 2 }
};